import { z } from 'zod';

export const MonthlyPlanStatusSchema = z.enum([
  'draft',
  'topics_pending',
  'content_pending',
  'active',
  'archived',
]);
export type MonthlyPlanStatus = z.infer<typeof MonthlyPlanStatusSchema>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

export const CreateMonthlyPlanSchema = z
  .object({
    hotelId: z.string().guid(),
    year: z.number().int().min(2024).max(2100),
    month: z.number().int().min(1).max(12),
    /** Inclusive start of the period. Optional; defaults to the 1st of (year, month). */
    startDate: isoDate.optional(),
    /** Inclusive end of the period. Optional; defaults to the last day of (year, month). */
    endDate: isoDate.optional(),
    brief: z.string().max(4000).optional(),
    focusKeywords: z.array(z.string().min(1).max(60)).max(20).default([]),
    brandSalesRatioOverride: z.number().min(0).max(1).nullable().optional(),
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type CreateMonthlyPlan = z.infer<typeof CreateMonthlyPlanSchema>;

// Partial update — only the editable fields. year/month/hotelId are immutable.
export const UpdateMonthlyPlanSchema = z
  .object({
    startDate: isoDate.optional(),
    endDate: isoDate.optional(),
    brief: z.string().max(4000).optional(),
    focusKeywords: z.array(z.string().min(1).max(60)).max(20).optional(),
    brandSalesRatioOverride: z.number().min(0).max(1).nullable().optional(),
  })
  // Only enforce ordering when BOTH dates are present in the partial patch —
  // a one-sided update (e.g. just endDate) can't be range-checked here.
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type UpdateMonthlyPlan = z.infer<typeof UpdateMonthlyPlanSchema>;

export const MonthlyPlanSchema = z.object({
  id: z.string().guid(),
  hotelId: z.string().guid(),
  year: z.number().int(),
  month: z.number().int(),
  startDate: isoDate,
  endDate: isoDate,
  brief: z.string().nullable(),
  focusKeywords: z.array(z.string()),
  brandSalesRatioOverride: z.number().nullable(),
  status: MonthlyPlanStatusSchema,
  createdBy: z.string().guid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MonthlyPlan = z.infer<typeof MonthlyPlanSchema>;
